import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Sparkles, Inbox, FileSearch, GraduationCap, Gauge, ArrowRight,
  Printer, LogIn, ChevronUp, ChevronDown, Users, Building2, Lock, Bot,
  Timer, Bell, FileDown, Flag, BarChart3, UserCheck, UploadCloud, CheckCircle2,
  Layers, Repeat, Workflow, Rocket, Target, Quote,
} from "lucide-react";

const BRAND = "rgb(var(--brand))";

/* --------------------------------- helpers --------------------------------- */
const Slide = ({ id, index, dark, children }) => (
  <section
    id={id}
    data-testid={`pitch-slide-${index}`}
    className={`pitch-slide relative flex min-h-screen w-full flex-col justify-center px-6 py-20 sm:px-12 lg:px-24 ${
      dark ? "text-white" : "text-foreground"
    }`}
    style={dark ? { background: BRAND } : {}}
  >
    <div className="mx-auto w-full max-w-6xl">{children}</div>
    <span className={`pitch-hide absolute bottom-6 left-8 text-xs ${dark ? "text-white/50" : "text-muted-foreground"}`}>
      {String(index).padStart(2, "0")} / 12
    </span>
  </section>
);

const Eyebrow = ({ children, dark }) => (
  <p className={`mb-4 text-xs font-semibold uppercase tracking-[0.2em] ${dark ? "text-white/70" : "brand-text"}`}>
    {children}
  </p>
);

const FeatureCard = ({ icon: Icon, title, desc }) => (
  <div className="rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:shadow-md">
    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-secondary brand-text">
      <Icon className="h-5 w-5" />
    </div>
    <h4 className="font-heading text-base font-semibold">{title}</h4>
    <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
  </div>
);

const Stat = ({ big, label, dark }) => (
  <div>
    <p className="font-heading text-4xl font-extrabold sm:text-5xl">{big}</p>
    <p className={`mt-1 text-sm ${dark ? "text-white/70" : "text-muted-foreground"}`}>{label}</p>
  </div>
);

/* ---------------------------------- deck ----------------------------------- */
const SLIDES = [
  "cover", "problem", "solution", "gateways", "governance", "capabilities",
  "roles", "multitenant", "security", "impact", "roadmap", "cta",
];

export default function Pitch() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [active, setActive] = useState(0);

  const goTo = (i) => {
    const clamped = Math.max(0, Math.min(SLIDES.length - 1, i));
    document.getElementById(SLIDES[clamped])?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const idx = Math.round(el.scrollTop / window.innerHeight);
      setActive(Math.max(0, Math.min(SLIDES.length - 1, idx)));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowDown", "PageDown", " "].includes(e.key)) { e.preventDefault(); goTo(active + 1); }
      if (["ArrowUp", "PageUp"].includes(e.key)) { e.preventDefault(); goTo(active - 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <div className="relative h-screen bg-background">
      {/* top progress */}
      <div className="pitch-hide fixed left-0 top-0 z-30 h-1 w-full bg-black/5">
        <div className="h-full brand-bg transition-all duration-300" style={{ width: `${((active + 1) / SLIDES.length) * 100}%` }} />
      </div>

      {/* controls */}
      <div className="pitch-hide fixed right-5 top-5 z-30 flex items-center gap-2">
        <Button data-testid="pitch-print" size="sm" variant="outline" onClick={() => window.print()} className="bg-white/90 backdrop-blur">
          <Printer className="mr-1 h-4 w-4" /> Save as PDF
        </Button>
        <Button data-testid="pitch-enter" size="sm" onClick={() => navigate("/login")} className="brand-bg text-white">
          <LogIn className="mr-1 h-4 w-4" /> Enter platform
        </Button>
      </div>

      {/* dot nav */}
      <div className="pitch-hide fixed right-5 top-1/2 z-30 hidden -translate-y-1/2 flex-col gap-2 md:flex">
        {SLIDES.map((s, i) => (
          <button key={s} aria-label={`Go to slide ${i + 1}`} onClick={() => goTo(i)}
            className={`h-2.5 w-2.5 rounded-full transition-all ${i === active ? "brand-bg scale-125" : "bg-muted-foreground/30 hover:bg-muted-foreground/60"}`} />
        ))}
      </div>

      {/* prev / next */}
      <div className="pitch-hide fixed bottom-5 right-5 z-30 flex flex-col gap-2">
        <Button data-testid="pitch-prev" size="icon" variant="outline" onClick={() => goTo(active - 1)} disabled={active === 0} className="bg-white/90 backdrop-blur">
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button data-testid="pitch-next" size="icon" variant="outline" onClick={() => goTo(active + 1)} disabled={active === SLIDES.length - 1} className="bg-white/90 backdrop-blur">
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>

      <div ref={containerRef} className="pitch-scroll h-screen overflow-y-auto scroll-smooth" style={{ scrollSnapType: "y mandatory" }} data-testid="pitch-deck">

        {/* 1 — Cover */}
        <Slide id="cover" index={1} dark>
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-9 w-9" />
            <span className="font-heading text-xl font-bold">AI-Supported Research Supervision</span>
          </div>
          <h1 className="mt-10 font-heading text-5xl font-extrabold leading-tight tracking-tight sm:text-6xl lg:text-7xl">
            AI prepares.<br />Humans decide.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85">
            A multi-tenant platform that runs the entire postgraduate research journey — from admission to graduation —
            with governed AI assistance and accountable human oversight at every step.
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm text-white/75">
            <Sparkles className="h-4 w-4" /> Admissions · Concept-Paper Gateway · Supervision Workspace · Workload & QA
          </div>
        </Slide>

        {/* 2 — Problem */}
        <Slide id="problem" index={2}>
          <Eyebrow>The problem</Eyebrow>
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">Research supervision doesn't scale — and quality slips</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <FeatureCard icon={Timer} title="Slow turnarounds" desc="Chapters and applications sit for weeks with no visibility and no accountability." />
            <FeatureCard icon={FileSearch} title="Inconsistent feedback" desc="Quality depends on who reviews, with no shared rubric or standard." />
            <FeatureCard icon={Inbox} title="Opaque admissions" desc="Manual document checks and eligibility decisions that are hard to audit." />
            <FeatureCard icon={ShieldCheck} title="Integrity risk" desc="Plagiarism and AI-generated text slip through without early signals." />
            <FeatureCard icon={Gauge} title="No oversight" desc="Directors can't see who is overloaded or where students are stuck." />
            <FeatureCard icon={Users} title="Supervisor overload" desc="Allocations are ad-hoc, ignoring expertise fit and current workload." />
          </div>
        </Slide>

        {/* 3 — Solution */}
        <Slide id="solution" index={3} dark>
          <Eyebrow dark>The solution</Eyebrow>
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">One governed platform for the whole journey</h2>
          <p className="mt-6 max-w-3xl text-lg text-white/85">
            AI assistants prepare rubric-based diagnostics, recommendations and drafts — but every binding decision
            is made by a qualified human, and every action is logged. The result: faster, fairer, fully auditable supervision.
          </p>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            <Stat dark big="4" label="Integrated gateways, one workflow" />
            <Stat dark big="8" label="Roles with tailored dashboards" />
            <Stat dark big="100%" label="Decisions kept in human hands" />
          </div>
        </Slide>

        {/* 4 — Gateways */}
        <Slide id="gateways" index={4}>
          <Eyebrow>How it works</Eyebrow>
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">Four gateways, end to end</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <FeatureCard icon={Inbox} title="1 · Admissions" desc="Public self-upload portal, AI fast-track eligibility review, cohort import, and admit-to-supervision in one click." />
            <FeatureCard icon={FileSearch} title="2 · Concept-Paper Gateway" desc="AI diagnostics against your rubric, integrity signals, and a human approval decision before a candidate proceeds." />
            <FeatureCard icon={GraduationCap} title="3 · Supervision Workspace" desc="Sequential chapter submission, structured feedback, milestones, and a clear next-step for every student." />
            <FeatureCard icon={Gauge} title="4 · Workload & QA Dashboard" desc="Turnaround SLAs, overdue queues, delay flags, and a supervisor performance leaderboard for directors." />
          </div>
        </Slide>

        {/* 5 — Governance */}
        <Slide id="governance" index={5}>
          <Eyebrow>Trust by design</Eyebrow>
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">Human-in-the-loop AI governance</h2>
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-6">
              <Bot className="h-6 w-6 brand-text" />
              <h4 className="mt-3 font-heading text-lg font-semibold">AI prepares</h4>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 brand-text" /> Rubric-based concept-paper & chapter diagnostics</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 brand-text" /> Admission eligibility parsing & recommendations</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 brand-text" /> Best-fit supervisor suggestions (expertise + workload)</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 brand-text" /> Integrity & originality signals</li>
              </ul>
            </div>
            <div className="rounded-xl border-2 brand-border bg-secondary/40 p-6">
              <UserCheck className="h-6 w-6 brand-text" />
              <h4 className="mt-3 font-heading text-lg font-semibold">Humans decide</h4>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 brand-text" /> Every admission, approval & grade is a human decision</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 brand-text" /> AI advice can be accepted, modified or overridden</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 brand-text" /> Accept-rate & override analytics prove human control</li>
                <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 brand-text" /> Complete, immutable audit trail of who did what</li>
              </ul>
            </div>
          </div>
        </Slide>

        {/* 6 — Capabilities */}
        <Slide id="capabilities" index={6}>
          <Eyebrow>Full capability set</Eyebrow>
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">Everything the process needs</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <FeatureCard icon={UploadCloud} title="AI fast-track admissions" desc="Parse applicant documents and recommend eligibility in seconds." />
            <FeatureCard icon={Layers} title="Cohort import" desc="Bulk-upload applicants from CSV/Excel and auto-email upload links." />
            <FeatureCard icon={FileSearch} title="Concept-paper AI review" desc="Rubric scoring, strengths, gaps and integrity flags." />
            <FeatureCard icon={Repeat} title="Sequential chapter gating" desc="Each chapter unlocks only after the previous is approved." />
            <FeatureCard icon={UserCheck} title="AI best-fit supervisor" desc="Ranked, one-click allocation on admit and reassignment." />
            <FeatureCard icon={Workflow} title="Reassignment + handover" desc="Move students with a handover note passed to the new supervisor." />
            <FeatureCard icon={Timer} title="Turnaround SLAs" desc="Configurable review windows with overdue queues." />
            <FeatureCard icon={Bell} title="Alerts & email" desc="In-app notifications plus branded email updates." />
            <FeatureCard icon={FileDown} title="PDF reports" desc="Server-generated integrity & feedback reports, emailed to students." />
            <FeatureCard icon={Flag} title="Director oversight" desc="Track activity and raise delay flags on stuck work." />
            <FeatureCard icon={BarChart3} title="Analytics & leaderboard" desc="Workload, throughput and QA performance at a glance." />
            <FeatureCard icon={Bot} title="Governed AI" desc="One universal AI engine, fully auditable and human-gated." />
          </div>
        </Slide>

        {/* 7 — Roles */}
        <Slide id="roles" index={7} dark>
          <Eyebrow dark>Built for everyone</Eyebrow>
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">A tailored dashboard for every role</h2>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Platform Owner", "Onboard & manage institutions"],
              ["Institution Admin", "Branding, users, rubrics & SLAs"],
              ["Director of Research", "Oversight, flags & performance"],
              ["Registrar", "Admissions & cohort intake"],
              ["Research Office", "Day-to-day operations"],
              ["Supervisor", "Review queue & feedback"],
              ["Student", "Progress & next step"],
              ["Quality Assurance", "Standards & audit"],
            ].map(([r, d]) => (
              <div key={r} className="rounded-lg border border-white/20 bg-white/10 p-4">
                <p className="font-heading font-semibold">{r}</p>
                <p className="mt-1 text-sm text-white/75">{d}</p>
              </div>
            ))}
          </div>
        </Slide>

        {/* 8 — Multi-tenant */}
        <Slide id="multitenant" index={8}>
          <Eyebrow>For any institution</Eyebrow>
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">Multi-tenant & fully customizable</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <FeatureCard icon={Building2} title="White-label branding" desc="Each institution gets its own logo, colours and identity." />
            <FeatureCard icon={Inbox} title="Per-programme criteria" desc="Configure documents & qualifications per programme." />
            <FeatureCard icon={Layers} title="Reusable templates" desc="Save admission-criteria presets (Master's, PhD, MPhil, PGDip…)." />
            <FeatureCard icon={Gauge} title="Configurable rubrics & SLAs" desc="Tune scoring rubrics and turnaround targets to your policy." />
          </div>
          <p className="mt-8 max-w-3xl text-muted-foreground">
            One deployment serves many universities with complete data isolation — onboard a new institution in minutes.
          </p>
        </Slide>

        {/* 9 — Security */}
        <Slide id="security" index={9}>
          <Eyebrow>Enterprise-ready</Eyebrow>
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">Security, privacy & accountability</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <FeatureCard icon={Lock} title="Role-based access" desc="Granular RBAC across eight roles and every route." />
            <FeatureCard icon={Building2} title="Tenant isolation" desc="Strict data separation between institutions." />
            <FeatureCard icon={FileSearch} title="Full audit trail" desc="Every consequential action is logged for compliance." />
            <FeatureCard icon={UploadCloud} title="Secure document storage" desc="Access-scoped object storage for all uploads." />
            <FeatureCard icon={ShieldCheck} title="Forced first-login reset" desc="Temporary passwords must be changed on first sign-in." />
            <FeatureCard icon={UserCheck} title="Human decision record" desc="Provable human control over every AI-assisted outcome." />
          </div>
        </Slide>

        {/* 10 — Impact */}
        <Slide id="impact" index={10} dark>
          <Eyebrow dark>The impact</Eyebrow>
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">Faster, fairer, fully transparent</h2>
          <div className="mt-10 grid gap-8 sm:grid-cols-3">
            <Stat dark big="Days → hours" label="Concept-paper & chapter turnaround with AI-prepared reviews" />
            <Stat dark big="Zero blind spots" label="Overdue queues & flags surface stuck work instantly" />
            <Stat dark big="One source of truth" label="Admissions, supervision & QA in a single audited system" />
          </div>
          <div className="mt-12 flex items-start gap-3 rounded-xl border border-white/20 bg-white/10 p-6">
            <Quote className="h-6 w-6 shrink-0 text-white/70" />
            <p className="text-lg italic text-white/90">
              “The AI does the preparation; our academics keep the judgement. That's exactly the balance
              modern research supervision needs.”
            </p>
          </div>
        </Slide>

        {/* 11 — Roadmap */}
        <Slide id="roadmap" index={11}>
          <Eyebrow>What's next</Eyebrow>
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">A platform that keeps growing</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <FeatureCard icon={Gauge} title="Capacity heatmap" desc="Spot overloaded supervisors before allocations pile up." />
            <FeatureCard icon={Lock} title="Session controls" desc="Revoke other devices instantly on password change." />
            <FeatureCard icon={BarChart3} title="Deeper analytics" desc="Cohort completion, time-to-degree and QA trends." />
            <FeatureCard icon={Layers} title="Flexible imports" desc="Map any spreadsheet columns during cohort intake." />
            <FeatureCard icon={Bell} title="Live cohort progress" desc="Track document completion per intake in real time." />
            <FeatureCard icon={Workflow} title="Open integrations" desc="Connect to existing SIS, LMS and identity providers." />
          </div>
        </Slide>

        {/* 12 — CTA */}
        <Slide id="cta" index={12} dark>
          <div className="flex items-center gap-3">
            <Rocket className="h-8 w-8" />
            <span className="font-heading text-lg font-bold">Ready when you are</span>
          </div>
          <h2 className="mt-8 font-heading text-4xl font-extrabold sm:text-5xl">
            Bring AI-supported supervision to your institution
          </h2>
          <p className="mt-6 max-w-2xl text-lg text-white/85">
            Launch a branded, multi-tenant research supervision workspace with governed AI and accountable human oversight —
            configured to your programmes, rubrics and policies.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button data-testid="pitch-cta-demo" size="lg" onClick={() => navigate("/login")} className="bg-white text-[color:var(--brand-fallback,#166534)] hover:opacity-90" style={{ color: BRAND }}>
              Request a demo <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <Button data-testid="pitch-cta-print" size="lg" variant="outline" onClick={() => window.print()} className="border-white/40 bg-transparent text-white hover:bg-white/10">
              <Printer className="mr-1 h-4 w-4" /> Save this deck as PDF
            </Button>
          </div>
          <div className="mt-10 flex items-center gap-2 text-sm text-white/70">
            <Target className="h-4 w-4" /> AI prepares · Humans decide
          </div>
        </Slide>
      </div>

      {/* print styles: show every slide stacked, hide navigation chrome */}
      <style>{`
        @media print {
          .pitch-hide { display: none !important; }
          .pitch-scroll { height: auto !important; overflow: visible !important; }
          .pitch-slide { min-height: auto !important; page-break-after: always; padding: 40px !important; }
          @page { size: A4 landscape; margin: 12mm; }
        }
        .pitch-slide { scroll-snap-align: start; }
      `}</style>
    </div>
  );
}
